import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./OurServicesIndex.css";
import Breadcrumbs from "../navigation/Breadcrumbs";
import { getServiceIndexBreadcrumbs } from "../navigation/breadcrumbUtils";
import { getBreadcrumbSchema } from "../navigation/breadcrumbSchema";
import SEO from "../seo/SEO";
import {
  SERVICE_ROUTES,
  SERVICE_VIEW_ROUTES,
} from "../../constants/serviceRoutes";

const services = [
  {
    title: "Data Science with AI",
    description:
      "Leverage advanced AI and data-driven solutions to automate processes, uncover business insights, improve decision-making, and drive operational efficiency across your organization.",
    path: SERVICE_ROUTES.dataScienceAi,
  },
  {
    title: "Object Detection",
    description:
      "Implement intelligent visual recognition systems for real-time object detection, monitoring, automation, quality inspection, and security-focused applications.",
    path: SERVICE_ROUTES.objectDetection,
  },
  {
    title: "Edge Computing",
    description:
      "Deploy low-latency edge computing architectures that process data closer to devices, improving performance, scalability, and real-time responsiveness.",
    path: SERVICE_ROUTES.edgeComputing,
  },
  {
    title: "IoT Integration",
    description:
      "Connect devices, sensors, and platforms through secure IoT ecosystems that enable real-time monitoring, automation, predictive maintenance, and smarter business operations.",
    path: SERVICE_ROUTES.iotIntegration,
  },
  {
    title: "Cloud Security",
    description:
      "Secure and optimize cloud environments with scalable infrastructure, compliance-focused architectures, identity management, and proactive cloud security solutions.",
    path: SERVICE_ROUTES.cloudSecurity,
  },
  {
    title: "Data Visualization",
    description:
      "Transform complex business data into interactive dashboards, actionable insights, and real-time analytics that support smarter strategic decisions.",
    path: SERVICE_ROUTES.dataVisualization,
  },
  {
    title: "UI/UX Design",
    description:
      "Design intuitive, engaging, and user-centric digital experiences that improve usability, customer satisfaction, and product adoption across web and mobile platforms.",
    path: SERVICE_ROUTES.uiUxDesign,
  },
  {
    title: "Mobile Development",
    description:
      "Build high-performance, scalable, and feature-rich mobile applications for Android and iOS platforms tailored to modern business requirements.",
    path: SERVICE_ROUTES.mobileDevelopment,
  },
  {
    title: "AI & ML Services",
    description:
      "Develop intelligent machine learning models and AI-powered systems that automate workflows, enhance personalization, and improve business productivity.",
    path: SERVICE_ROUTES.aiMlServices,
  },
  {
    title: "Data Science",
    description:
      "Build robust data pipelines, storage systems, and data management solutions that ensure reliability, scalability, and seamless access to business-critical information.",
    path: SERVICE_ROUTES.dataScience,
  },
];

const payrollServices = [
  {
    title: "Payroll Services",
    description:
      "End-to-end payroll support for accurate salary management, compliance, and employee payments.",
    path: SERVICE_ROUTES.payrollServices,
  },
  {
    title: "Payroll Software",
    description:
      "Secure payroll software solutions that simplify calculations, records, and workforce payments.",
    path: SERVICE_ROUTES.payrollSoftware,
  },
  {
    title: "Payroll Processing",
    description:
      "Reliable payroll processing services that help businesses handle salary cycles with confidence.",
    path: SERVICE_ROUTES.payrollProcessing,
  },
];
const hrServices = [
  {
    title: "BPO Services",
    description:
      "Scalable business process outsourcing solutions designed to improve operational efficiency, reduce costs, and support business growth through dedicated support teams.",
    path: SERVICE_ROUTES.bpoServices,
  },
  {
    title: "Content Moderation",
    description:
      "Professional content moderation services for text, image, video, and user-generated content to ensure platform safety, compliance, and brand protection.",
    path: SERVICE_ROUTES.contentModeration,
  },
  {
    title: "Customer Support",
    description:
      "Multi-channel customer support solutions including chat, email, and voice support designed to enhance customer satisfaction and business responsiveness.",
    path: SERVICE_ROUTES.customerSupport,
  },
  {
    title: "HR & Payroll Operations",
    description:
      "Comprehensive HR and payroll management services including employee onboarding, attendance tracking, payroll processing, compliance, and workforce operations.",
    path: SERVICE_ROUTES.hrPayrollOperations,
  },
  {
    title: "Recruitment Process Outsourcing",
    description:
      "End-to-end recruitment solutions covering candidate sourcing, screening, interview coordination, onboarding, and talent acquisition support.",
    path: SERVICE_ROUTES.recruitmentProcessOutsourcing,
  },
  {
    title: "Back Office Operations",
    description:
      "Reliable back-office support services that streamline administrative processes, operational workflows, documentation management, and business coordination.",
    path: SERVICE_ROUTES.backOfficeOperations,
  },
  {
    title: "Data Entry Services",
    description:
      "Accurate and secure data entry solutions for digital records management, data processing, document handling, and large-scale information organization.",
    path: SERVICE_ROUTES.dataEntryServices,
  },
  {
    title: "Technical Support",
    description:
      "Dedicated technical support services for troubleshooting, issue resolution, system assistance, and IT operational support across multiple platforms.",
    path: SERVICE_ROUTES.technicalSupport,
  },
];

const healthServices = [
  {
    title: "Medical Coding",
    description:
      "Accurate, compliant medical coding support for clinical records, reimbursement, audits, and healthcare reporting.",
    path: SERVICE_ROUTES.medicalCoding,
  },
  {
    title: "Medical Billing",
    description:
      "Reliable medical billing support for claims preparation, payment posting, statements, and patient account management.",
    path: SERVICE_ROUTES.medicalBilling,
  },
  {
    title: "Revenue Cycle Management (RCM)",
    description:
      "End-to-end revenue cycle support that improves cash flow, reduces revenue leakage, and strengthens financial performance.",
    path: SERVICE_ROUTES.revenueCycleManagement,
  },
  {
    title: "Claims Management",
    description:
      "Structured claims validation, tracking, correction, payer follow-up, and appeal support for faster resolution.",
    path: SERVICE_ROUTES.claimsManagement,
  },
  {
    title: "Insurance Eligibility Verification",
    description:
      "Pre-service coverage and benefit verification that helps prevent claim issues and improves patient registration.",
    path: SERVICE_ROUTES.insuranceEligibilityVerification,
  },
];

const getServiceViewFromPath = (pathname) => {
  if (pathname === SERVICE_VIEW_ROUTES.hr) {
    return "hr";
  }

  if (pathname === SERVICE_VIEW_ROUTES.payroll) {
    return "payroll";
  }

  if (pathname === SERVICE_VIEW_ROUTES.health) {
    return "health";
  }

  return "services";
};

const OurServicesIndex = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeCards, setActiveCards] = useState(
    getServiceViewFromPath(location.pathname),
  );
  const headingGroupRef = useRef(null);

  useEffect(() => {
    setActiveCards(getServiceViewFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    const handlePageClick = (event) => {
      if (!headingGroupRef.current?.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePageClick);

    return () => {
      document.removeEventListener("mousedown", handlePageClick);
    };
  }, []);

  const selectServiceView = (view) => {
    setActiveCards(view);
    setIsDropdownOpen(false);
    navigate(SERVICE_VIEW_ROUTES[view]);
  };

  const handleServiceCardClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const visibleServices =
    activeCards === "hr"
      ? hrServices
      : activeCards === "payroll"
        ? payrollServices
        : activeCards === "health"
          ? healthServices
          : services;
  const pageTitle =
    activeCards === "hr"
      ? "HR services"
      : activeCards === "payroll"
        ? "Payroll services"
        : activeCards === "health"
          ? "Health services"
          : "Our services";
  const pageDescription =
    activeCards === "hr"
      ? "BlackRoth HR services help organizations manage people, insights, and partner workflows with clarity and confidence. We support businesses with practical HR solutions, workforce intelligence, and connected systems that improve operations and create better employee experiences."
      : activeCards === "payroll"
        ? "BlackRoth payroll services help businesses manage payroll operations with accuracy, security, and compliance. From payroll software to processing support, we simplify salary workflows and help teams run dependable payment cycles."
        : activeCards === "health"
          ? "BlackRoth health services help healthcare organizations simplify essential administrative and revenue workflows. From medical coding and billing to claims management and eligibility verification, we deliver accurate, secure, and dependable operational support."
          : "At BlackRoth, we deliver innovative technology and business solutions designed to help organizations scale, optimize operations, and accelerate growth. From AI-powered systems and cloud infrastructure to cybersecurity, digital transformation, and business support services, we combine industry expertise with modern technology to create impactful and future-ready solutions tailored to your business needs.";
  const sectionTitle =
    activeCards === "hr"
      ? "Explore HR services"
      : activeCards === "payroll"
        ? "Explore payroll services"
        : activeCards === "health"
          ? "Explore health services"
          : "Explore our services";

  const breadcrumbItems = getServiceIndexBreadcrumbs(location.pathname);
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);

  return (
    <div className="our-services-index">
      <SEO schema={breadcrumbSchema} />
      <section
        className={`our-services-index__hero${
          activeCards === "hr" ? " our-services-index__hero--hr" : ""
        }${
          activeCards === "payroll" ? " our-services-index__hero--payroll" : ""
        }${
          activeCards === "health" ? " our-services-index__hero--health" : ""
        }`}
      >
        <Breadcrumbs items={breadcrumbItems} />

        <div className="our-services-index__heading-group" ref={headingGroupRef}>
          <h1>{pageTitle}</h1>

          <button
            type="button"
            className={`our-services-index__dropdown-toggle${
              isDropdownOpen ? " is-open" : ""
            }`}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-label="Open service menu"
            aria-expanded={isDropdownOpen}
          >
            <span aria-hidden="true"></span>
          </button>

          {isDropdownOpen && (
            <div className="our-services-index__dropdown">
              {activeCards !== "services" && (
                <button
                  type="button"
                  onClick={() => selectServiceView("services")}
                >
                  Our Services
                </button>
              )}
              {activeCards !== "hr" && (
                <button
                  type="button"
                  onClick={() => selectServiceView("hr")}
                >
                  HR Services
                </button>
              )}
              {activeCards !== "payroll" && (
                <button
                  type="button"
                  onClick={() => selectServiceView("payroll")}
                >
                  Payroll Services
                </button>
              )}
              {activeCards !== "health" && (
                <button
                  type="button"
                  onClick={() => selectServiceView("health")}
                >
                  Health Services
                </button>
              )}
            </div>
          )}
        </div>

        <p className="our-services-index__intro">{pageDescription}</p>
      </section>

      <section className="our-services-index__explore">
        <h2>{sectionTitle}</h2>
        <p>
          At BlackRoth, we combine innovation, technology, and vision to create powerful digital experiences 
          that help businesses move forward and achieve more.
        </p>
      </section>

      <section className="our-services-index__grid" aria-label="Service links">
        {visibleServices.map((service) => (
          <Link
            key={service.path}
            className="our-services-index__card"
            to={service.path}
            state={{ servicesIndexView: activeCards }}
            onClick={handleServiceCardClick}
          >
            <h2>{service.title}</h2>
            <p>{service.description}</p>
            <span>Explore service</span>
          </Link>
        ))}
      </section>
    </div>
  );
};

export default OurServicesIndex;
